package di

import org.koin.dsl.module
import data.repository.*
import data.datasource.local.*
import data.datasource.remote.*
import data.storage.*
import data.network.*
import domain.repository.*
import domain.usecase.*
import presentation.viewmodel.AppViewModel

/**
 * Koin DI module for the entire application
 * Provides all dependencies in a clean, testable way
 */
val appModule = module {
    
    // Storage and Network - Basic dependencies
    single { createSecureStorage() }
    single { createPlatformApi() }
    
    // Authentication Repository - Core dependency
    single<AuthenticationRepository> { 
        AuthenticationRepositoryImpl(
            secureStorage = get(),
            platformApi = get()
        )
    }
    
    // Network Client
    single { AuthenticatedHttpClient(authRepository = get()) }
    
    // Theme Repository
    single<ThemeRepository> { 
        ThemeRepositoryImpl(
            secureStorage = get()
        )
    }
    
    // Data Sources - Simplified
    single { ActionLocalDataSourceImpl() }
    single { 
        GitHubKtorRemoteDataSourceImpl(
            authRepository = get()
        )
    }
    single { 
        GitHubGraphQLRemoteDataSource(
            authenticatedHttpClient = get()
        )
    }
    
    // Repositories - Add explicit type hints
    single<GitHubRepository> { 
        GitHubRepositoryImpl(
            remoteDataSource = get<GitHubKtorRemoteDataSourceImpl>(),
            authRepository = get<AuthenticationRepository>()
        )
    }
    
    single<ActionRepository> { 
        ActionRepositoryImpl(
            localDataSource = get<ActionLocalDataSourceImpl>()
        )
    }
    
    single<GitHubInsightsRepository> { 
        GitHubInsightsRepositoryImpl(
            graphqlDataSource = get<GitHubGraphQLRemoteDataSource>()
        )
    }
    
    // Use Cases - Add explicit type hints
    single { ManageActionsUseCase(repository = get<ActionRepository>()) }
    single { GetPullRequestsUseCase(repository = get<GitHubRepository>()) }
    single { GetPRInsightsUseCase(repository = get<GitHubInsightsRepository>()) }
    single { LoginUseCase(authRepository = get<AuthenticationRepository>()) }
    single { LogoutUseCase(authRepository = get<AuthenticationRepository>()) }
    single { ObserveAuthenticationStateUseCase(authRepository = get<AuthenticationRepository>()) }
    single { ObserveThemeUseCase(themeRepository = get<ThemeRepository>()) }
    single { ChangeThemeUseCase(themeRepository = get<ThemeRepository>()) }
    single { DiagnoseOrganizationAccessUseCase(repository = get<GitHubInsightsRepository>()) }
    
    // ViewModels - Add explicit type hints for all dependencies
    single { 
        AppViewModel(
            manageActionsUseCase = get<ManageActionsUseCase>(),
            loginUseCase = get<LoginUseCase>(),
            logoutUseCase = get<LogoutUseCase>(),
            observeAuthenticationStateUseCase = get<ObserveAuthenticationStateUseCase>(),
            getPullRequestsUseCase = get<GetPullRequestsUseCase>(),
            observeThemeUseCase = get<ObserveThemeUseCase>(),
            changeThemeUseCase = get<ChangeThemeUseCase>(),
            diagnoseOrganizationAccessUseCase = get<DiagnoseOrganizationAccessUseCase>(),
            getPRInsightsUseCase = get<GetPRInsightsUseCase>()
        )
    }
}